import * as React from "react";
import { Button } from "./Button";
import { useAuth } from "./AuthProvider";
import { Brand } from "./Brand";
import { TokenScreen, readTokenLink } from "./TokenScreen";

// LoginScreen — rebuilt on v1's auth layout: centred brand, a gradient card, generous field
// spacing, a full-width accent submit. The first version was an unstyled box on a grey page.
//
// It also answers an emailed password-reset or invite link, which is why nothing changed at the
// three call sites: the check lives HERE, so every console that shows a sign-in form handles the
// link by construction. Putting it in the three Gates instead would be the same code three
// times, and the one that got forgotten would be the console the person happened to land in.
//
// See TokenScreen for the measurement: reset was broken on both nodes, for every role, because
// `/` was flipped from v1 to v2 and v2 never had the route. Nothing along that path reports a
// fault — the mail sends, the link resolves, the page renders, the server answers 200.

export interface LoginScreenProps {
  title: string;
  subtitle?: string;
  errorMessage?: string;
  /** Rendered under the form — the admin console links recovery mode here. */
  footer?: React.ReactNode;
}

export function LoginScreen({
  title,
  subtitle,
  errorMessage = "That email address and password did not match.",
  footer,
}: LoginScreenProps) {
  const { client, login, verifyTwoFactor } = useAuth();
  // Read ONCE, on mount. Re-reading on every render would tear the form down mid-typing if
  // anything else touched the URL.
  const [tokenLink, setTokenLink] = React.useState(() => readTokenLink());
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  // The second factor, and the "forgot password" path. Both were absent: a person who enabled
  // 2FA — with the control this panel offers — could not sign in at all, and a person who
  // forgot their password had no way to ask for a link even though the endpoint has always
  // existed and the mail it sends works.
  const [needCode, setNeedCode] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [sentReset, setSentReset] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (needCode) {
        await verifyTwoFactor(code);
        return;
      }
      if ((await login(email, password)) === "2fa_required") {
        setNeedCode(true);
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : errorMessage);
    } finally {
      setSubmitting(false);
    }
  }

  if (tokenLink) {
    return (
      <TokenScreen
        purpose={tokenLink.purpose}
        token={tokenLink.token}
        onDone={() => {
          // Clear the token out of the address bar before showing the sign-in form. It is spent,
          // and a used credential sitting in history, in a shared screenshot or in a referrer is
          // a credential leaked for no benefit.
          window.history.replaceState(null, "", window.location.pathname);
          setTokenLink(null);
        }}
      />
    );
  }

  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-[390px]">
        <div className="mb-7 text-center">
          <div className="mb-4 flex justify-center">
            <Brand />
          </div>
          <h1 className="text-[19px] font-[680] tracking-[-0.02em]">{title}</h1>
          {subtitle && <p className="mt-1.5 text-[14px] text-muted">{subtitle}</p>}
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-panel border border-line bg-gradient-to-b from-surface to-bg2 p-[26px]"
        >
          {error && (
            <p className="mb-4 rounded-lg border border-[color-mix(in_oklab,var(--ozp-crit)_40%,transparent)] bg-critsoft px-3 py-2.5 text-[13px] text-crit">
              {error}
            </p>
          )}
          {needCode ? (
            <>
              <p className="mb-4 text-[13px] leading-relaxed text-muted">
                Enter the six-digit code from your authenticator app. A recovery code works here
                too, if you cannot reach the app.
              </p>
              <Field label="Code" htmlFor="code">
                <input
                  id="code"
                  inputMode="text"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full rounded-[9px] border border-line2 bg-bg px-[13px] py-[11px] font-mono text-[15px] text-fg outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--ozp-accent-soft)]"
                />
              </Field>
            </>
          ) : (
          <>
          <Field label="Email" htmlFor="email">
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-[9px] border border-line2 bg-bg px-[13px] py-[11px] text-[15px] text-fg transition-[border-color,box-shadow] outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--ozp-accent-soft)]"
            />
          </Field>
          <Field label="Password" htmlFor="password">
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-[9px] border border-line2 bg-bg px-[13px] py-[11px] text-[15px] text-fg transition-[border-color,box-shadow] outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--ozp-accent-soft)]"
            />
          </Field>
          </>
          )}
          <Button type="submit" variant="accent" disabled={submitting} className="mt-1 w-full py-3 text-[15px]">
            {submitting ? "Signing in…" : needCode ? "Confirm code" : "Sign in"}
          </Button>
          {!needCode && (
            <div className="mt-3 text-center text-[12.5px]">
              {sentReset ? (
                <span className="text-muted">
                  If that address has an account here, a reset link is on its way.
                </span>
              ) : (
                <button
                  type="button"
                  className="text-accent hover:underline disabled:opacity-50"
                  disabled={!email.trim()}
                  onClick={async () => {
                    try {
                      // The answer is the same whether the address exists or not — the endpoint
                      // is written that way on purpose, so this must not report anything more
                      // specific and turn the panel into an account oracle.
                      await client.apiFetch<unknown>("/auth/password/reset-request", {
                        method: "POST",
                        body: JSON.stringify({ email: email.trim() }),
                      });
                    } catch {
                      /* deliberately ignored: see above */
                    }
                    setSentReset(true);
                  }}
                >
                  Forgot your password?
                </button>
              )}
            </div>
          )}
        </form>
        {footer && <div className="mt-4 text-center text-[12.5px] text-muted">{footer}</div>}
      </div>
    </main>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="mb-[15px]">
      <label htmlFor={htmlFor} className="mb-[7px] block text-[12px] font-medium text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
